import * as React from 'react';
import { useMemo } from 'react';
import * as R from 'ramda';
import Card from '../../layout/card/Card';
import { Str } from '../../localization/translationKeys';
import { css } from '@emotion/core';
import { COLORS } from '../../globalStyle/colors';
import Msg from '../../localization/Msg';
import { ProjStr } from '../messages/ProjStr';
import ProjMsg from '../messages/ProjMsg';
import { FLEX_COLUMN } from '../../layout/flexBox';
import { FeedbackSurveyQuestion } from './models';
import LocalDiv from '../../localization/LocalDiv';
import { DevPlanItem } from '../devPlan/models';
import CreateSurveyViewAccordian from './CreateSurveyViewAccordian';


const styles = {
  card: css`
    padding: 14px;
    border: 3px solid ${COLORS.DARK_GREY};
    flex-basis: 50%;
    height: min-content;
  `,
  darkbg: css`
    background-color: ${COLORS.DARK_GREY};
  `,
  headerText: css`
    padding: 0 0 14px;
    font-size: 12px;
    font-weight: bold;
    color: ${COLORS.WHITE};
  `,
  separator: css`
    margin: 5px 0 20px;
    border-bottom: 1px solid ${COLORS.MEDIUM_GREY}
  `,
  requiredQuestion: css`
    margin: 0 0 14px;
    color: ${COLORS.WHITE};
  `,
  accordian: css`
    color: ${COLORS.WHITE};
    margin-bottom: 14px;
  `,
};


type Props = {
  requiredQuestions: FeedbackSurveyQuestion[];
  devItems: DevPlanItem[];
  addByCompetency: boolean;
  className?: string;
  deleteQuestion: (questionId: number) => void;
  deleteQuestionsForCompetency: (competencyId: number) => void;
};


/**
 * CreateSurvey: a component that required items for a feedback survey
 *
 * PureComponent: true
 * Redux Connected: false
 */
const CreateSurveyRequired: React.FC<Props> = props => {

  const { numericQuestions, textQuestions } = useMemo(
    () => ({
      numericQuestions: props.requiredQuestions.filter(q => !q.textQuestion),
      textQuestions:    props.requiredQuestions.filter(q => q.textQuestion),
    }),
    [props],
  );

  const requiredText = useMemo(
    () => (
      R.map(
        (question: FeedbackSurveyQuestion) => (
          <LocalDiv css={styles.requiredQuestion} key={`${question.questionId}`}>
              {question.questionText}
          </LocalDiv>
        ),
        textQuestions,
      )
    ),
    [textQuestions],
  );

  return (
    <Card css={[FLEX_COLUMN, styles.card, styles.darkbg]}>
      {
        numericQuestions.length > 0 && (
          <>
            <ProjMsg str={ProjStr.RequiredForSurvey} css={[FLEX_COLUMN, styles.headerText]}/>
            <LocalDiv css={styles.accordian}>
              <CreateSurveyViewAccordian 
                questions={numericQuestions}
                devItems={props.devItems}
                addByCompetency={props.addByCompetency}
                showDelete={true}
                darkBackground={true}
                addedStyle={false}
                deleteQuestion={props.deleteQuestion}
                deleteQuestionsForCompetency={props.deleteQuestionsForCompetency}
                createdMode={false}
              />
            </LocalDiv>
          </>
        )
      }
      {
        numericQuestions.length > 0 && textQuestions.length > 0 && (
          <LocalDiv css={styles.separator}></LocalDiv>
        )
      }
      {
        textQuestions.length > 0 && (
          <>
            <Msg str={Str.RequiredOpenQuestions} css={[FLEX_COLUMN, styles.headerText]}/>
            <LocalDiv>
              {requiredText}
            </LocalDiv>
          </>
        )
      }

    </Card>
  )
};

export default CreateSurveyRequired;
